import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { z } from "zod";

import i18n from "../../../config/i18n";
import { VALIDATION } from "../../../config/constants";
import {
  getNationalLengthForCountry,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import { alphaOnly } from "../../../utils/InputValidation";
import { type Country } from "../../../utils/countries";
import CountryCodeSelect from "../../common/CountryCodeSelect";
import OtpInput from "./OtpInput";

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const getPhoneStepSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z
    .object({
      countryCode: z.string().min(2).max(3),
      phoneNumber: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!requiresStrictNationalLength(data.countryCode)) return;
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneRequired"),
          path: ["phoneNumber"],
        });
        return;
      }
      const expected = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.phoneInvalid"),
          path: ["phoneNumber"],
        });
      }
    });
};

const getProfileStepSchema = () => {
  const t = (key: string) => i18n.t(key);
  return z.object({
    email: z
      .string()
      .min(1, t("validation.emailRequired"))
      .email(t("validation.emailInvalid")),
    firstName: z
      .string()
      .min(1, t("validation.firstNameRequired"))
      .max(50),
    lastName: z
      .string()
      .min(1, t("validation.lastNameRequired"))
      .max(50),
    password: z
      .string()
      .min(VALIDATION.PASSWORD_MIN_LENGTH, t("validation.passwordMin")),
    acceptTerms: z.boolean().refine((v) => v === true, {
      message: t("validation.acceptTerms"),
    }),
  });
};

type PhoneStepData = z.infer<ReturnType<typeof getPhoneStepSchema>>;
type ProfileStepData = z.infer<ReturnType<typeof getProfileStepSchema>>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PhoneRegisterFormProps {
  onSubmit: (data: {
    phoneNumber: string;
    countryCode: string;
    email: string;
    otp: string;
    firstName: string;
    lastName: string;
    password: string;
    confirmPassword: string;
    referralCode?: string;
  }) => Promise<void>;
  onSendOtp: (phone: string, countryCode: string) => Promise<void>;
  onVerifyOtp: (phone: string, countryCode: string, otp: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

const ErrorHelperText: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        color: "error.main",
      }}
    >
      <ErrorOutline sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {message}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const STEPS = ["auth.stepPhone", "auth.stepVerify", "auth.stepProfile"];

const PhoneRegisterForm: React.FC<PhoneRegisterFormProps> = ({
  onSubmit,
  onSendOtp,
  onVerifyOtp,
  isLoading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const referralCodeFromUrl = searchParams.get("ref") || "";

  const [activeStep, setActiveStep] = useState(0);
  const [e164Phone, setE164Phone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(true);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // --- Step 1 form ---
  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    watch: watchPhone,
    setValue: setPhoneValue,
    formState: { errors: phoneErrors, touchedFields: phoneTouched, isSubmitted: phoneIsSubmitted },
  } = useForm<PhoneStepData>({
    resolver: zodResolver(getPhoneStepSchema()),
    defaultValues: { countryCode: "TJ", phoneNumber: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const countryCode = watchPhone("countryCode");
  const maxLen = getNationalLengthForCountry(countryCode);
  const phoneErrorVisible =
    !!phoneErrors.phoneNumber &&
    (phoneTouched.phoneNumber || phoneIsSubmitted);

  // --- Step 3 form ---
  const {
    register: profileRegister,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isValid: profileIsValid },
  } = useForm<ProfileStepData>({
    resolver: zodResolver(getProfileStepSchema()),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      acceptTerms: false,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const handleCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "TJ";
    setPhoneValue("countryCode", code, {
      shouldDirty: true,
      shouldValidate: false,
    });
    const trimmed = trimPhoneToNationalLength(
      code,
      watchPhone("phoneNumber") || ""
    );
    setPhoneValue("phoneNumber", trimmed, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  // Step 1 → Step 2: send OTP
  const handleSendCode = async (data: PhoneStepData) => {
    const phone = toE164(data.countryCode, data.phoneNumber);
    setE164Phone(phone);
    setIsSendingOtp(true);
    setSendError(null);
    try {
      await onSendOtp(phone, data.countryCode);
      setActiveStep(1);
    } catch (err: unknown) {
      // Show error from backend (e.g., "PHONE_ALREADY_REGISTERED")
      const msg = err instanceof Error ? err.message : String(err);
      setSendError(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 2: OTP verified — MUST call backend to verify code
  const handleOtpComplete = async (code: string) => {
    setOtp(code);
    setOtpError(null);
    try {
      await onVerifyOtp(e164Phone, countryCode, code);
      setActiveStep(2);
    } catch {
      setOtpError(t("auth.invalidOtp"));
    }
  };

  const handleResendOtp = async () => {
    setIsSendingOtp(true);
    try {
      await onSendOtp(e164Phone, countryCode);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 3: final submit
  const handleFinalSubmit = async (data: ProfileStepData) => {
    await onSubmit({
      phoneNumber: e164Phone,
      countryCode,
      email: data.email,
      otp,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      confirmPassword: data.password,
      referralCode: referralCodeFromUrl || undefined,
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((key) => (
          <Step key={key}>
            <StepLabel>{t(key)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* ------------------------------------------------------------------ */}
      {/* Step 1 — Phone Number                                               */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 0 && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handlePhoneSubmit(handleSendCode)();
          }}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          noValidate
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.enterPhoneDesc")}
          </Typography>

          <Controller
            name="phoneNumber"
            control={phoneControl}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  label={t("auth.phoneNumber")}
                  className="notranslate"
                  translate="no"
                  fullWidth
                  required
                  autoComplete="tel-national"
                  error={phoneErrorVisible}
                  disabled={isSendingOtp || isLoading}
                  onChange={(e) => {
                    const trimmed = trimPhoneToNationalLength(
                      countryCode,
                      e.target.value || ""
                    );
                    field.onChange(trimmed);
                  }}
                  onBlur={field.onBlur}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1, borderRight: "1px solid #ccc", mr: 1 }}>
                          <img
                            loading="lazy"
                            width={20}
                            height={14}
                            src="https://flagcdn.com/w20/tj.png"
                            srcSet="https://flagcdn.com/w40/tj.png 2x"
                            alt="Tajikistan"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            +992
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: maxLen,
                  }}
                />
                <ErrorHelperText
                  message={
                    phoneErrorVisible
                      ? phoneErrors.phoneNumber?.message
                      : undefined
                  }
                />
                {!phoneErrorVisible && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mt: 0.5, display: "block" }}>
                    {t("auth.tajikistanOnlyHint")}
                  </Typography>
                )}
              </Box>
            )}
          />

          {error && <Alert severity="error">{error}</Alert>}
          {sendError && (
            <Alert severity="error">{t(`apiErrors.${sendError}`, sendError)}</Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className="notranslate"
            translate="no"
            disabled={isSendingOtp || isLoading}
            sx={{ fontWeight: 600, py: 1.2 }}
          >
            {isSendingOtp ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>{t("auth.sendingCode")}</span>
              </Box>
            ) : (
              t("auth.sendOtp")
            )}
          </Button>
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 2 — OTP Verification                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center" }}
            className="notranslate"
            translate="no"
          >
            {t("auth.codeSentTo", { phone: e164Phone })}
          </Typography>

          <OtpInput
            onComplete={handleOtpComplete}
            onResend={handleResendOtp}
            error={otpError || error}
            isLoading={isLoading || isSendingOtp}
          />

          <Button
            variant="text"
            size="small"
            onClick={() => setActiveStep(0)}
            disabled={isLoading}
            className="notranslate"
            translate="no"
          >
            {t("auth.back")}
          </Button>
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 3 — Complete Profile                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeStep === 2 && (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleProfileSubmit(handleFinalSubmit)();
          }}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          noValidate
        >
          <Box>
            <TextField
              {...profileRegister("email")}
              label={t("auth.email")}
              type="email"
              className="notranslate"
              translate="no"
              fullWidth
              required
              autoComplete="email"
              error={!!profileErrors.email}
            />
            <ErrorHelperText message={profileErrors.email?.message} />
          </Box>
          <Box>
            <TextField
              {...profileRegister("firstName")}
              label={t("auth.firstName")}
              className="notranslate"
              translate="no"
              fullWidth
              required
              autoComplete="given-name"
              error={!!profileErrors.firstName}
              disabled={isLoading}
              onChange={(e) => {
                const ev = e as React.ChangeEvent<HTMLInputElement>;
                profileRegister("firstName").onChange({
                  target: { name: "firstName", value: alphaOnly(ev.target.value || "") },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            />
            <ErrorHelperText message={profileErrors.firstName?.message} />
          </Box>

          <Box>
            <TextField
              {...profileRegister("lastName")}
              label={t("auth.lastName")}
              className="notranslate"
              translate="no"
              fullWidth
              required
              autoComplete="family-name"
              error={!!profileErrors.lastName}
              disabled={isLoading}
              onChange={(e) => {
                const ev = e as React.ChangeEvent<HTMLInputElement>;
                profileRegister("lastName").onChange({
                  target: { name: "lastName", value: alphaOnly(ev.target.value || "") },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            />
            <ErrorHelperText message={profileErrors.lastName?.message} />
          </Box>

          <Box>
            <TextField
              {...profileRegister("password")}
              label={t("auth.password")}
              className="notranslate"
              translate="no"
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              error={!!profileErrors.password}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      disabled={isLoading}
                      aria-label={
                        showPassword ? t("auth.hidePassword") : t("auth.showPassword")
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ErrorHelperText message={profileErrors.password?.message} />
            {!profileErrors.password && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mt: 0.5 }}>
                {t("validation.passwordMinSimple")}
              </Typography>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                {...profileRegister("acceptTerms")}
                color="primary"
              />
            }
            label={
              <Typography
                variant="body2"
                className="notranslate"
                translate="no"
              >
                {t("auth.iAgreeToThe")}{" "}
                <Link
                  component={RouterLink}
                  to="/terms"
                  target="_blank"
                  color="inherit"
                  underline="always"
                >
                  {t("auth.termsOfService")}
                </Link>{" "}
                {t("auth.and")}{" "}
                <Link
                  component={RouterLink}
                  to="/privacyPolicy"
                  target="_blank"
                  color="inherit"
                  underline="always"
                >
                  {t("auth.privacyPolicy")}
                </Link>
              </Typography>
            }
          />
          {profileErrors.acceptTerms && (
            <Typography
              variant="caption"
              color="error"
              className="notranslate"
              translate="no"
              sx={{ ml: 2 }}
            >
              {t("auth.pleaseAcceptTerms")}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            className="notranslate"
            translate="no"
            disabled={isLoading}
            sx={{ fontWeight: 600, py: 1.2 }}
          >
            {isLoading ? (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>{t("auth.creatingAccount")}</span>
              </Box>
            ) : (
              t("auth.createAccountButton")
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PhoneRegisterForm;
